import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { CheckCircle, Error, Info, Refresh } from '@mui/icons-material';
import authService from '../services/authService';
import { useSearchParams } from 'react-router-dom';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get email from location state if available
  const email = location.state?.email || '';
  
  // State for verification status
  const [status, setStatus] = useState('loading'); // loading, success, error, pending-verification
  const [message, setMessage] = useState('');

  // Wrap verifyEmail in useCallback to avoid infinite loops
  const verifyEmail = useCallback(async () => {
    const token = searchParams.get('token');
    
    // If no token in URL, don't try to verify
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided. Please check your email for the verification link.');
      return;
    }
    
    setStatus('loading');
    setMessage('Verifying your email address...');
    
    try {
      const response = await authService.verifyEmail(token);
      
      if (response && response.success) {
        setStatus('success');
        setMessage('Your email has been successfully verified! You can now log in to your account.');
      } else {
        throw new Error(response?.message || 'Email verification failed.');
      }
    } catch (error) {
      setStatus('error');
      console.error('Email verification error:', error);
      setMessage(error.message || 'Failed to verify email. The link may have expired or is invalid.');
    }
  }, [searchParams]);
  
  // Function to handle resending verification email
  const handleResendVerification = async () => {
    if (!email) {
      setMessage('No email address provided. Please go back to login and try again.');
      return;
    }
    
    setStatus('loading');
    setMessage('Sending verification email...');
    
    try {
      const response = await authService.resendVerificationEmail(email);
      
      if (response && response.success) {
        setStatus('pending-verification');
        setMessage(`Verification email sent to ${email}. Please check your inbox.`);
      } else {
        throw new Error(response?.message || 'Failed to send verification email.');
      }
    } catch (error) {
      setStatus('error');
      console.error('Error sending verification email:', error);
      setMessage(error.message || 'Failed to send verification email. Please try again.');
    }
  };
  
  // Now the useEffect hook will work without warnings
  useEffect(() => {
    // If we received an email through navigation state but no token in URL,
    // then we're here because the user needs to verify their email
    if (!searchParams.get('token') && email) {
      setStatus('pending-verification');
      setMessage(`We've sent a verification email to ${email}. Please check your inbox and click the verification link.`);
    } else {
      // Normal verification flow with token in URL
      verifyEmail();
    }
  }, [searchParams, email, verifyEmail]);
  
  return (
    <Box 
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 4
      }}
    >
      <Typography variant="h4" gutterBottom>
        Email Verification
      </Typography>
      
      {status === 'loading' && (
        <Box textAlign="center" py={4}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="body1">{message}</Typography>
        </Box>
      )}
      
      {status === 'success' && (
        <Box textAlign="center" py={4}>
          <CheckCircle 
            sx={{ 
              fontSize: 60, 
              color: 'success.main',
              mb: 2 
            }} 
          />
          <Typography variant="body1" color="success.main" gutterBottom>
            {message}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Box>
      )}
      
      {status === 'pending-verification' && (
        <Box textAlign="center" py={4}>
          <Info 
            sx={{ 
              fontSize: 60, 
              color: 'info.main',
              mb: 2 
            }} 
          />
          <Typography variant="body1" color="info.main" gutterBottom>
            {message}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleResendVerification}
            sx={{ mt: 2 }}
          >
            Resend Verification Email
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{ mt: 2, ml: 2 }}
          >
            Back to Login
          </Button>
        </Box>
      )}
      
      {status === 'error' && (
        <Box textAlign="center" py={4}>
          <Error 
            sx={{ 
              fontSize: 60, 
              color: 'error.main',
              mb: 2 
            }} 
          />
          <Typography variant="body1" color="error.main" gutterBottom>
            {message}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Back to Login
          </Button>
          {email && (
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleResendVerification}
              sx={{ mt: 2, ml: 2 }}
            >
              Resend Verification Email
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VerifyEmail;